import { useState } from "react"
import { useNavigate } from "react-router-dom"
import { useForm } from "@tanstack/react-form"
import { ArrowLeft } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { useRegisterWaterbureau, useRetryProvisioning } from "@/hooks/api"
import { useAuth } from "@/hooks/useAuth"
import useCurrentUser from "@/hooks/use-current-user"
import { useToast } from "@/hooks/use-toast"
import type { WaterbureauRegistrationResponse } from "@/gen/api"
import {
  ADMIN_PERMISSION_OBJECTS,
  AdminPermissionObject,
  allInheritanceObjects,
  hasPermission,
} from "@/lib/admin-permissions"

const environments = ["dev", "test", "prod"]

const getRegistrationErrorMessage = (error: unknown) => {
  if (error && typeof error === "object" && "response" in error) {
    const response = (error as { response?: { data?: { message?: unknown } } }).response
    if (typeof response?.data?.message === "string") {
      if (response.data.message.includes("Backend address already linked")) {
        return "This backend address is already connected to another Water Bureau. Use a different backend address, or update the existing Water Bureau instead."
      }
      return response.data.message
    }
  }

  return "An error occurred"
}

const makeCode = (name: string) => {
  const base = name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
  const suffix = Math.random().toString(36).slice(2, 8)
  return `${base || "water-bureau"}-${suffix}`
}

export default function RegisterWaterbureauPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { hasRole } = useCurrentUser()
  const { toast } = useToast()
  const registerMutation = useRegisterWaterbureau()
  const retryProvisioningMutation = useRetryProvisioning()
  const [result, setResult] = useState<WaterbureauRegistrationResponse | null>(null)
  const backPath = "/manage-waterbureau"
  const provisioningStatus = result?.provisioningStatus
  const canRegisterWaterBureaus =
    hasRole("super_admin") || hasPermission(user?.permissions, "water_bureaus", "write_all")

  const form = useForm({
    defaultValues: {
      name: "",
      description: "",
      backendUrl: "",
      environment: "dev",
      inheritedPermissionObjects: allInheritanceObjects() as AdminPermissionObject[],
    },
    onSubmit: async ({ value }) => {
      if (!canRegisterWaterBureaus) return
      try {
        const response = await registerMutation.mutateAsync({
          code: makeCode(value.name),
          name: value.name,
          backendUrl: value.backendUrl,
          environment: value.environment.toUpperCase(),
          inheritedPermissionObjects: value.inheritedPermissionObjects,
          ...(value.description ? { description: value.description } : {}),
        })
        setResult(response.data || null)
        toast({
          title: "Water Bureau Registered",
          description: `${value.name} has been registered successfully.`,
        })
      } catch (error) {
        toast({
          title: "Registration Failed",
          description: getRegistrationErrorMessage(error),
          variant: "destructive",
        })
      }
    },
  })

  const handleRetryProvisioning = async () => {
    if (!result?.councilCode) return
    try {
      const status = await retryProvisioningMutation.mutateAsync(result.councilCode)
      setResult({ ...result, provisioningStatus: status })
      toast({
        title: status === "PROVISIONED" ? "Provisioning Complete" : "Provisioning Retried",
        description:
          status === "PROVISIONED"
            ? "The Water Bureau backend is now connected."
            : `Provisioning status is ${formatProvisioningStatus(status)}.`,
      })
    } catch (error) {
      toast({
        title: "Provisioning Retry Failed",
        description: getRegistrationErrorMessage(error),
        variant: "destructive",
      })
    }
  }

  if (!canRegisterWaterBureaus) {
    return (
      <div className="space-y-6">
        <PageHeader onBack={() => navigate(backPath)} />
        <div className="rounded-lg border bg-card p-6">
          <h2 className="text-xl font-semibold">Access denied</h2>
          <p className="mt-2 text-muted-foreground">
            You need Water Bureaus Write All permission to register a Water Bureau.
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <PageHeader onBack={() => navigate(backPath)} />

      {!result ? (
        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            void form.handleSubmit().catch(() => {
              // Registration errors are handled by the form onSubmit toast.
            })
          }}
          className="space-y-6"
        >
          <div className="rounded-lg border bg-card p-6">
            <div className="grid gap-4 md:grid-cols-2">
              <form.Field
                name="name"
                validators={{
                  onChange: ({ value }) => (!value ? "Name is required" : undefined),
                }}
              >
                {(field) => (
                    <TextField
                      id="wb-name"
                      label="Name"
                      value={field.state.value}
                      onBlur={field.handleBlur}
                      onChange={field.handleChange}
                      error={field.state.meta.errors[0]}
                      placeholder="e.g. Midcoast Water"
                    />
                  )}
                </form.Field>

              <form.Field
                name="backendUrl"
                validators={{
                  onChange: ({ value }) => (!value ? "Backend URL is required" : undefined),
                }}
              >
                {(field) => (
                  <TextField
                    id="wb-backend"
                    label="Backend Address"
                    type="url"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={field.handleChange}
                    error={field.state.meta.errors[0]}
                    placeholder="https://api.midcoast.waterbureau.com.au"
                  />
                )}
              </form.Field>

              <form.Field name="description">
                {(field) => (
                  <TextField
                    id="wb-description"
                    label="Description (Optional)"
                    value={field.state.value}
                    onBlur={field.handleBlur}
                    onChange={field.handleChange}
                    placeholder="Short note for your team"
                  />
                )}
              </form.Field>

              <form.Field name="environment">
                {(field) => (
                  <div className="space-y-2">
                    <Label htmlFor="wb-environment">Environment</Label>
                    <Select value={field.state.value} onValueChange={field.handleChange}>
                      <SelectTrigger id="wb-environment">
                        <SelectValue placeholder="Select environment" />
                      </SelectTrigger>
                      <SelectContent>
                        {environments.map((environment) => (
                          <SelectItem key={environment} value={environment}>
                            {environment.toUpperCase()}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
              </form.Field>
            </div>
          </div>

          <form.Field name="inheritedPermissionObjects">
            {(field) => (
              <div className="space-y-3 rounded-lg border bg-card p-6">
                <div>
                  <Label>Access to Share</Label>
                  <p className="text-sm text-muted-foreground">
                    Choose which admin areas should be available for this Water Bureau. Super admins
                    always keep full access.
                  </p>
                </div>
                <div className="grid gap-3 md:grid-cols-2">
                  {ADMIN_PERMISSION_OBJECTS.map((permission) => {
                    const id = `inherit-${permission.object}`
                    return (
                      <div key={permission.object} className="flex items-center gap-2">
                        <Checkbox
                          id={id}
                          checked={field.state.value.includes(permission.object)}
                          onCheckedChange={(checked) =>
                            field.handleChange(
                              checked === true
                                ? [...new Set([...field.state.value, permission.object])]
                                : field.state.value.filter(
                                    (object) => object !== permission.object,
                                  ),
                            )
                          }
                        />
                        <Label htmlFor={id}>{permission.label}</Label>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}
          </form.Field>

          <div className="flex justify-end gap-x-2">
            <Button type="button" variant="outline" onClick={() => navigate(backPath)}>
              Cancel
            </Button>
            <form.Subscribe selector={(state) => [state.canSubmit, state.isSubmitting]}>
              {([canSubmit, isSubmitting]) => (
                <Button type="submit" disabled={!canSubmit || isSubmitting}>
                  {isSubmitting ? "Creating..." : "Create Water Bureau"}
                </Button>
              )}
            </form.Subscribe>
          </div>
        </form>
        ) : (
          <div className="space-y-5 rounded-lg border bg-card p-6">
            <ProvisioningStatusBanner
              status={provisioningStatus}
              onRetry={handleRetryProvisioning}
              retrying={retryProvisioningMutation.isPending}
              councilName={result.councilName}
            />

            <div className="grid gap-4 md:grid-cols-2">
              <ReadOnlyValue label="Name" value={result.councilName} />
              <ReadOnlyValue label="Water Bureau Code" value={result.councilCode} mono />
              <ReadOnlyValue label="Record Status" value={formatRecordStatus(result.status)} />
              <ReadOnlyValue label="Connection" value={formatConnectionStatus(provisioningStatus)} />
            </div>

            <div className="border-t pt-4 space-y-3">
              <ReadOnlyValue label="Connection Reference" value={result.clientId} mono muted />
            </div>

          <div className="flex justify-end gap-x-2 pt-4">
            <Button onClick={() => navigate(backPath)}>Done</Button>
          </div>
        </div>
      )}
    </div>
  )
}

function ProvisioningStatusBanner({
  status,
  onRetry,
  retrying,
  councilName,
}: {
  status?: string
  onRetry: () => void
  retrying: boolean
  councilName?: string
}) {
  if (status === "PROVISIONED") {
    return (
      <div className="rounded-md border border-green-200 bg-green-50 p-4">
        <p className="text-sm font-medium text-green-800">Setup complete</p>
        <p className="mt-1 text-xs text-green-600">
          {councilName || "This Water Bureau"} is ready to use. No further steps are needed.
        </p>
      </div>
    )
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <p className="text-sm font-medium text-amber-800">Waiting for backend</p>
          <p className="mt-1 text-xs text-amber-700">
            {councilName || "This Water Bureau"} was created, but its backend hasn't responded yet.
            Make sure the backend is online and try again.
          </p>
        </div>
        <Button type="button" variant="outline" size="sm" onClick={onRetry} disabled={retrying}>
          {retrying ? "Checking..." : "Try Again"}
        </Button>
      </div>
    </div>
  )
}

function formatProvisioningStatus(status?: string) {
  return status ? status.replace(/_/g, " ") : "UNKNOWN"
}

function formatRecordStatus(status?: string) {
  if (!status) return "—"
  const map: Record<string, string> = {
    ACTIVE: "Active",
    INACTIVE: "Inactive",
    SUSPENDED: "Suspended",
  }
  return map[status] || status
}

function formatConnectionStatus(status?: string) {
  if (!status) return "—"
  if (status === "PROVISIONED") return "Connected"
  if (status === "PROVISION_FAILED") return "Not connected"
  if (status === "PROVISION_EXPIRED") return "Expired"
  if (status === "PENDING") return "In progress"
  return status.replace(/_/g, " ")
}

function PageHeader({ onBack }: { onBack: () => void }) {
  return (
    <div className="flex items-center justify-between gap-4">
      <div>
        <Button variant="ghost" size="sm" className="mb-2 -ml-2" onClick={onBack}>
          <ArrowLeft className="mr-2 size-4" /> Back
        </Button>
        <h1 className="text-2xl font-bold">Register New Water Bureau</h1>
        <p className="text-muted-foreground">
          Add the Water Bureau details. We will connect the backend automatically when it is online.
        </p>
      </div>
    </div>
  )
}

function TextField({
  id,
  label,
  value,
  onBlur,
  onChange,
  error,
  placeholder,
  type = "text",
}: {
  id: string
  label: string
  value: string
  onBlur: () => void
  onChange: (value: string) => void
  error?: string
  placeholder?: string
  type?: string
}) {
  return (
    <div className="space-y-2">
      <Label htmlFor={id}>{label}</Label>
      <Input
        id={id}
        type={type}
        value={value}
        onBlur={onBlur}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
      />
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>
  )
}

function ReadOnlyValue({
  label,
  value,
  mono,
  muted,
}: {
  label: string
  value?: string | number
  mono?: boolean
  muted?: boolean
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <p
        className={`${mono ? "font-mono text-sm" : "font-medium"} ${muted ? "rounded bg-muted p-2" : ""}`}
      >
        {value}
      </p>
    </div>
  )
}
